import axios from 'axios';
import type { ModuleKey } from '@/modules/workspace/moduleManifests';
import { API_URL } from '@/services/apiConfig';


const api = axios.create({
    baseURL: API_URL,
    headers: { 'Content-Type': 'application/json' },
    // Sem isso, qualquer backend travado deixa a request pendente para sempre
    // e a tela presa no loading (QA ISSUE-008/003). Chamadas longas (uploads,
    // IA) passam timeout próprio por chamada, sobrescrevendo este default.
    timeout: 60000,
});

function decodeJwtPayload(token: string): Record<string, unknown> | null {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;

    try {
        const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
        const decoded = atob(padded);
        return JSON.parse(decoded) as Record<string, unknown>;
    } catch {
        return null;
    }
}

function clearStoredAuth() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
}

function redirectToLogin(reason?: string) {
    const params = new URLSearchParams(window.location.search);
    if (reason) {
        params.set('auth_reason', reason);
    } else {
        params.delete('auth_reason');
    }
    const query = params.toString();
    window.location.href = query ? `/login?${query}` : '/login';
}

function getUsableToken(): string | null {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const payload = decodeJwtPayload(token);
    if (!payload) {
        clearStoredAuth();
        return null;
    }

    const exp = Number(payload.exp);
    if (Number.isFinite(exp) && exp * 1000 <= Date.now()) {
        clearStoredAuth();
        return null;
    }

    return token;
}

// Add token to all requests
api.interceptors.request.use((config) => {
    const token = getUsableToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Redirect to login on 401 — only for auth session routes, not module/dashboard routes
api.interceptors.response.use(
    (res) => res,
    (err) => {
        const url = err.config?.url || '';
        const status = err.response?.status;
        // Only force-logout if the session verification itself fails
        // Module routes (/v2/...) returning 401 just means no access — use fallback
        const isAuthRoute = url.includes('/auth/me') || url.includes('/auth/login') || url.includes('/auth/register');
        const isModuleRoute = url.startsWith('/v2/');
        if (status === 401 && !isAuthRoute && !isModuleRoute) {
            clearStoredAuth();
            window.location.href = '/login';
        }
        return Promise.reject(err);
    }
);

export interface AuthUser {
    id: string;
    email: string;
    name: string;
    role: string;
    branch_id?: string | null;
    company_id?: string | null;
    menuProfile?: "platform" | "tenant_admin" | "user";
    modules?: string[];
    permissions?: Array<{
        module_name: string;
        access_level: "readonly" | "create" | "edit" | "act";
    }>;
}

function normalizeRole(role?: string | null) {
    const normalized = String(role || "").toLowerCase();
    if (normalized === "owner" || normalized === "superadmin" || normalized === "platform_superadmin") {
        return "platform_superadmin";
    }
    if (normalized === "admin" || normalized === "tenant_admin") {
        return "tenant_admin";
    }
    return "user";
}

function resolveMenuProfile(user?: AuthUser | null): "platform" | "tenant_admin" | "user" {
    if (user?.menuProfile) return user.menuProfile;
    const role = normalizeRole(user?.role);
    if (role === "platform_superadmin") return "platform";
    if (role === "tenant_admin") return "tenant_admin";
    return "user";
}

export const authService = {
    register: async (email: string, password: string, name?: string): Promise<{ token: string; user: AuthUser }> => {
        const response = await api.post('/auth/register', { email, password, name });
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return { token, user };
    },

    login: async (email: string, password: string): Promise<{ token: string; user: AuthUser }> => {
        const response = await api.post('/auth/login', { email, password });
        const { token, user } = response.data;
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        return { token, user };
    },

    logout: () => {
        clearStoredAuth();
        redirectToLogin();
    },

    switchUser: () => {
        clearStoredAuth();
        redirectToLogin('switch-user');
    },

    getUser: (): AuthUser | null => {
        const raw = localStorage.getItem('user');
        if (!raw) return null;
        try { return JSON.parse(raw); } catch { return null; }
    },

    getToken: (): string | null => getUsableToken(),

    isAuthenticated: (): boolean => Boolean(getUsableToken()),

    getMenuProfile: (): "platform" | "tenant_admin" | "user" => {
        const user = authService.getUser();
        return resolveMenuProfile(user);
    },

    isPlatformSuperadmin: (): boolean => normalizeRole(authService.getUser()?.role) === "platform_superadmin",

    isTenantAdmin: (): boolean => normalizeRole(authService.getUser()?.role) === "tenant_admin",

    hasMenuProfile: (profile: "platform" | "tenant_admin" | "user"): boolean => authService.getMenuProfile() === profile,

    hasModuleAccess: (moduleKey: ModuleKey): boolean => {
        if (!getUsableToken()) return false;
        const user = authService.getUser();
        if (!user) return false;
        if (["platform_superadmin", "tenant_admin"].includes(normalizeRole(user.role))) return true;
        const moduleMap: Record<ModuleKey, string> = {
            patentes: 'PATENTES',
            marcas: 'MARCAS',
            di: 'DI',
        };
        const moduleName = moduleMap[moduleKey];
        const byPermission = user.permissions?.some(
            (permission) => permission.module_name === moduleName
        );
        if (byPermission) {
            return true;
        }
        return Array.isArray(user.modules) && user.modules.includes(moduleName);
    },
};

export { api };
